import { Button, Dialog, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { AlertCircle, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';

type Props = {
  isOpen: boolean;
  title: string;
  fileName?: string;
  downloadUrl?: string;
  onClose: () => void;
  onDownload: () => void;
};

export function CapitalGlobalPolicyDocumentModal({
  isOpen,
  title,
  fileName,
  downloadUrl,
  onClose,
  onDownload,
}: Props) {
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [hasPreviewError, setHasPreviewError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsPreviewLoading(true);
      setHasPreviewError(false);
    }
  }, [isOpen, downloadUrl]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='flex h-[85vh] w-[880px] max-w-[90vw] flex-col rounded-[24px] p-6 shadow-xl'>
          <div className='flex h-full flex-col gap-4'>
            <Dialog.Header className='w-full shrink-0 p-0'>
              <Dialog.Title className='w-full'>
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <FileText className='h-6 w-6 text-alba-black-500' />
                    <div className='flex flex-col'>
                      <Text className='text-lg font-bold text-alba-black-500'>
                        {title}
                      </Text>
                      {fileName && (
                        <Text className='text-xs text-alba-gray-600'>
                          {fileName}
                        </Text>
                      )}
                    </div>
                  </div>
                  <Dialog.CloseButton onClick={onClose} />
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='min-h-0 w-full flex-1 overflow-hidden px-0 py-0'>
              <div className='relative h-full w-full overflow-hidden rounded-xl border border-alba-gray-200 bg-alba-gray-100'>
                {!downloadUrl || hasPreviewError ? (
                  <div className='flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center'>
                    <AlertCircle className='h-8 w-8 text-alba-gray-500' />
                    <Text className='text-sm text-alba-gray-600'>
                      Não foi possível exibir a pré-visualização do documento.
                      Use o botão Download para abri-lo.
                    </Text>
                  </div>
                ) : (
                  <>
                    {isPreviewLoading && (
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <Spinner size='lg' colorScheme='blue' />
                      </div>
                    )}
                    <iframe
                      src={downloadUrl}
                      title={title}
                      className='h-full w-full border-0'
                      onLoad={() => setIsPreviewLoading(false)}
                      onError={() => {
                        setIsPreviewLoading(false);
                        setHasPreviewError(true);
                      }}
                    />
                  </>
                )}
              </div>
            </Dialog.Body>

            <Dialog.Footer className='w-full shrink-0 p-0'>
              <div className='flex w-full items-center justify-end gap-6'>
                <Dialog.CloseTrigger asChild>
                  <Button variant='ghost' size='md' onClick={onClose}>
                    Fechar
                  </Button>
                </Dialog.CloseTrigger>
                <Button variant='primary' size='md' onClick={onDownload}>
                  Download
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
